import React, { useEffect, useState } from 'react';
import {
  Coins,
  Wallet,
  ShieldCheck,
  Link2,
  Unlink,
  ExternalLink,
  AlertTriangle,
  Loader2,
  Check,
} from 'lucide-react';
import { api, type LedgerEntry, type WalletInfo } from '../lib/api';
import { BRAND } from '../brand';

/* ------------------------------------------------------------------ *
 * Acceso a la billetera del navegador
 * ------------------------------------------------------------------ */

interface Eip1193Provider {
  request(args: { method: string; params?: unknown[] }): Promise<unknown>;
}

declare global {
  interface Window {
    ethereum?: Eip1193Provider;
  }
}

const REASON_LABELS: Record<string, string> = {
  welcome_bonus: 'Bono de bienvenida',
  match_prize: 'Premio de partida',
  prize_pool_stake: 'Bote apostado',
  prize_pool_refund: 'Devolución de bote',
  daily_play: 'Partida del día',
  quiz_published: 'Quiz publicado',
  admin_adjust: 'Ajuste manual',
};

export const DahuWallet: React.FC<{
  balance: number;
  onBalanceChange: (n: number) => void;
  /** Menor de la edad de su país: la billetera cripto no se le ofrece. */
  restrictedMinor?: boolean;
}> = ({ balance, onBalanceChange, restrictedMinor }) => {
  const [ledger, setLedger] = useState<LedgerEntry[]>([]);
  const [wallet, setWallet] = useState<WalletInfo | null>(null);
  const [linking, setLinking] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [justLinked, setJustLinked] = useState(false);

  useEffect(() => {
    void (async () => {
      try {
        const data = await api.get<{ balance: number; ledger: LedgerEntry[] }>('/dahus');
        setLedger(data.ledger);
        onBalanceChange(data.balance);
        const w = await api.get<{ wallet: WalletInfo | null }>('/wallet');
        setWallet(w.wallet);
      } catch {
        /* la pantalla funciona sin el historial */
      }
    })();
  }, [onBalanceChange]);

  const linkWallet = async () => {
    setError(null);

    if (!window.ethereum) {
      setError(
        'No detectamos ninguna billetera en este navegador. Necesitas una extensión compatible con EVM.',
      );
      return;
    }

    setLinking(true);
    try {
      const accounts = (await window.ethereum.request({ method: 'eth_requestAccounts' })) as string[];
      const address = accounts?.[0];
      if (!address) throw new Error('No se seleccionó ninguna cuenta.');

      // El servidor emite un texto de un solo uso; la billetera lo firma.
      const { nonce, message } = await api.post<{ nonce: string; message: string }>('/wallet/nonce');

      const signature = (await window.ethereum.request({
        method: 'personal_sign',
        params: [message, address],
      })) as string;

      const result = await api.post<{ wallet: WalletInfo }>('/wallet/link', {
        address,
        signature,
        nonce,
      });

      setWallet(result.wallet);
      setJustLinked(true);
      setTimeout(() => setJustLinked(false), 3000);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'No se pudo vincular la billetera.';
      // Rechazar la firma es una decisión legítima, no un fallo que reportar.
      setError(/user rejected|denied/i.test(message) ? null : message);
    } finally {
      setLinking(false);
    }
  };

  const unlink = async () => {
    await api.delete('/wallet');
    setWallet(null);
  };

  return (
    <div className="max-w-3xl mx-auto px-4 py-8 space-y-6">
      {/* Saldo */}
      <div className="surface-raised p-6 text-center space-y-2">
        <p className="text-ink-400 text-sm uppercase tracking-wider">Tu saldo</p>
        <p className="text-5xl font-display font-black text-ember-400 font-mono tabular-nums">
          {balance.toLocaleString('es')}
        </p>
        <p className="text-ink-300">{BRAND.currencyName}</p>
      </div>

      {/* Qué son los Dahus — dicho sin rodeos */}
      <div className="surface p-5 space-y-2">
        <h3 className="font-semibold flex items-center gap-2">
          <Coins className="w-4 h-4 text-ember-400" />
          Qué son los {BRAND.currencyName}
        </h3>
        <p className="text-ink-300 text-sm leading-relaxed">
          Son puntos internos de {BRAND.name}. Sirven para poner bote en tus partidas y repartirlo
          entre quienes suban al podio.{' '}
          <span className="text-ink-100">
            No son una criptomoneda, no tienen valor monetario y no se pueden comprar, vender ni
            retirar.
          </span>{' '}
          Nadie puede gastar dinero real aquí.
        </p>
      </div>

      {/* Billetera. A un menor no se le ofrece: el servidor también lo impide. */}
      {!restrictedMinor && (
      <div className="surface p-5 space-y-4">
        <h3 className="font-semibold flex items-center gap-2">
          <Wallet className="w-4 h-4 text-sky-400" />
          Billetera vinculada
        </h3>

        {wallet ? (
          <div className="space-y-3">
            <div className="flex items-center gap-3 p-3 rounded-xl bg-ink-850 border border-ink-700">
              <ShieldCheck className="w-5 h-5 text-success-400 shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="font-mono text-sm text-ink-100 truncate">{wallet.address}</p>
                <p className="text-ink-500 text-xs">
                  Verificada el {new Date(wallet.linkedAt).toLocaleDateString('es')}
                </p>
              </div>
              {justLinked && <Check className="w-5 h-5 text-success-400" />}
            </div>
            <button
              onClick={unlink}
              className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium bg-ink-800 hover:bg-ink-700 border border-ink-600 text-ink-300 transition-colors"
            >
              <Unlink className="w-4 h-4" />
              Desvincular
            </button>
          </div>
        ) : (
          <>
            <p className="text-ink-300 text-sm leading-relaxed">
              Vincular una billetera es opcional y sirve solo para demostrar que esa dirección es
              tuya.
            </p>

            {/* Esto merece destacarse: es exactamente lo que la gente teme al pulsar. */}
            <ul className="text-sm space-y-1.5 text-ink-300">
              <li className="flex gap-2">
                <ShieldCheck className="w-4 h-4 text-success-400 shrink-0 mt-0.5" />
                Solo se te pide <strong className="text-ink-100">firmar un texto</strong>. Firmar no
                mueve fondos.
              </li>
              <li className="flex gap-2">
                <ShieldCheck className="w-4 h-4 text-success-400 shrink-0 mt-0.5" />
                Nunca te pediremos tu frase semilla ni tu clave privada.
              </li>
              <li className="flex gap-2">
                <ShieldCheck className="w-4 h-4 text-success-400 shrink-0 mt-0.5" />
                No custodiamos saldos ni solicitamos permisos de gasto.
              </li>
            </ul>

            <button
              onClick={linkWallet}
              disabled={linking}
              className="flex items-center gap-2 px-5 py-3 rounded-xl font-semibold bg-sky-600 hover:bg-sky-500 disabled:bg-ink-800 disabled:text-ink-500 text-white transition-colors"
            >
              {linking ? <Loader2 className="w-4 h-4 animate-spin" /> : <Link2 className="w-4 h-4" />}
              {linking ? 'Esperando tu firma…' : 'Vincular billetera'}
            </button>

            {!window.ethereum && (
              <p className="text-ink-500 text-xs flex items-center gap-1.5">
                <ExternalLink className="w-3 h-3" />
                Necesitas una extensión de billetera compatible con EVM.
              </p>
            )}
          </>
        )}

        {error && (
          <p className="flex items-start gap-2 text-danger-400 text-sm">
            <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
            {error}
          </p>
        )}
      </div>

      )}

      {/* Movimientos */}
      <div className="surface p-5">
        <h3 className="font-semibold mb-3">Movimientos</h3>
        {ledger.length === 0 ? (
          <p className="text-ink-500 text-sm">Todavía no hay movimientos.</p>
        ) : (
          <ul className="divide-y divide-ink-800">
            {ledger.map((entry, i) => (
              <li key={i} className="flex items-center gap-3 py-2.5">
                <span className="flex-1">
                  <span className="text-ink-100 text-sm">
                    {REASON_LABELS[entry.reason] ?? entry.reason}
                  </span>
                  <span className="block text-ink-500 text-xs">
                    {new Date(entry.created_at).toLocaleString('es')}
                    {entry.ref && ` · sala ${entry.ref}`}
                  </span>
                </span>
                <span
                  className={`font-mono tabular-nums font-semibold ${
                    entry.delta > 0 ? 'text-success-400' : 'text-danger-400'
                  }`}
                >
                  {entry.delta > 0 ? '+' : ''}
                  {entry.delta.toLocaleString('es')}
                </span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
};
